import { getAuthHeader } from './get-auth-header';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';
import { ResultOfAccessResponseViewModel } from 'bff/client/models/ResultOfAccessResponseViewModel';
import { Configuration, ResponseError, SecurityApi } from 'bff/client';
import { Env } from '@sanlam-glacier/app-common/types/env/Env.type.js';
import { AuthenticatedUser } from '@sanlam-glacier/app-common/types/auth/authenticated-user.type.js';
import { UserInfo } from '@sanlam-glacier/app-common/types/auth/user-info.type.js';
import { AuthNavigate } from '@sanlam-glacier/app-common/types/auth/auth-navigate.type.js';
import { AuthStore } from 'auth';
import { TokenResponse } from '@sanlam-glacier/app-common/types/auth/token-response.type.js';
import { UserWithFeatures } from 'types/auth/user-context.type';

type CreateGetAccessProps = {
  getSignedInUser: () => AuthenticatedUser | undefined;
  setSignedInUser: (user: UserInfo) => void;
  setCurrentAuthenticatedUser: (actingAsUser: UserWithFeatures | undefined) => void;
  env: Env;
  navigate: AuthNavigate;
};

const handleOnSuccess =
  (createGetAccessProps: CreateGetAccessProps) => (result: ResultOfAccessResponseViewModel) => {
    const { setSignedInUser, setCurrentAuthenticatedUser } = createGetAccessProps;

    AuthStore.setUserInfo({
      display_name: `${result.data?.userInformation.firstName} ${result.data?.userInformation.surname}`,
      email: result.data?.userInformation.emailAddress ?? '',
      family_name: result.data?.userInformation.surname ?? '',
      given_name: result.data?.userInformation.firstName ?? '',
      sub: result.data?.userInformation.sanpayCode ?? '',
      groups: '',
      guid: '',
      name: result.data?.userInformation.firstName ?? '',
      phone_number: '',
      preferred_username: '',
      nickname: '',
      login: result.data?.userInformation.sanpayCode ?? '',
      clientNumber: result.data?.userInformation.clientNumber
    });

    setSignedInUser({
      display_name: `${result.data?.userInformation.firstName} ${result.data?.userInformation.surname}`,
      email: result.data?.userInformation.emailAddress ?? '',
      family_name: result.data?.userInformation.surname ?? '',
      given_name: result.data?.userInformation.firstName ?? '',
      sub: result.data?.userInformation.sanpayCode ?? '',
      groups: '',
      guid: '',
      name: result.data?.userInformation.firstName ?? '',
      phone_number: '',
      preferred_username: '',
      nickname: '',
      login: result.data?.userInformation.sanpayCode ?? '',
      clientNumber: result.data?.userInformation.clientNumber
    });

    setCurrentAuthenticatedUser({
      features: result.data?.features ?? [],
      firstName: result.data?.userInformation.firstName ?? '',
      lastName: result.data?.userInformation.surname ?? '',
      intermediaryCode: result.data?.userInformation.sanpayCode ?? '',
      clientNumber: result.data?.userInformation.clientNumber ?? ''
    });
  };

export const getAccess =
  (createGetAccessProps: CreateGetAccessProps) =>
  async (
    authToken?: TokenResponse | undefined
  ): Promise<{ success: boolean; status?: number; correlationId?: string | null }> => {
    const { env } = createGetAccessProps;

    const config = new Configuration({
      basePath: replaceWithDynamicHost(env.apiUrl, env.apiHostMappings),
      apiKey: getAuthHeader(
        replaceWithDynamicHost(env.apiUrl, env.apiHostMappings),
        true,
        env.auth.client.jwtProvider,
        authToken
      ),
      accessToken: authToken?.access_token
    });

    const securityApi = new SecurityApi(config);

    try {
      const response = await securityApi.securityGetAccess();
      handleOnSuccess(createGetAccessProps)(response);
      return { success: true };
    } catch (error) {
      logger.error({ message: `${error}` });

      if (error instanceof ResponseError) {
        const correlationId = error.response?.headers?.get('X-Correlationid');
        return { success: false, status: error.response.status, correlationId };
      }

      return { success: false };
    }
  };
